/* eslint-disable no-console */
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useEffect, useState } from "react";

type VersionConfig = {
    latestiOSVersion: string;
    minimumSupportediOSVersion: string;
    latestAndroidVersion: string;
    minimumSupportedAndroidVersion: string;
    iosStoreUrl: string;
    androidStoreUrl: string;
    message?: string;
};

type UseNativeAppVersionCheckResult = {
    confirmUpdatePrompt: () => void;
    showUpdatePrompt: boolean;
    updateMessage: string;
    hideClose?: boolean;
    closeHandler?: () => void;
};

const compareVersions = (a: string, b: string): number => {
    const pa = a.split(".").map(Number);
    const pb = b.split(".").map(Number);

    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const na = pa[i] ?? 0;
        const nb = pb[i] ?? 0;
        if (na > nb) return 1;
        if (na < nb) return -1;
    }

    return 0;
};

export const useNativeAppVersionCheck = (): UseNativeAppVersionCheckResult => {
    const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
    const [updateMessage, setUpdateMessage] = useState("");
    const [storeUrl, setStoreUrl] = useState("");
    const [mustUpdate, setMustUpdate] = useState(false);

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        let isDisposed = false;

        const checkForUpdate = async () => {
            const info = await App.getInfo();
            const config = await fetch("/app-version.json").then(response => {
                if (!response.ok) {
                    throw new Error(`App version check failed with status ${response.status}`);
                }

                return response.json() as Promise<VersionConfig>;
            });

            if (isDisposed) return;

            const isIOS = Capacitor.getPlatform() === "ios";
            const currentVersion = info.version;
            const latestVersion = isIOS ? config.latestiOSVersion : config.latestAndroidVersion;
            const minimumSupportedVersion = isIOS
                ? config.minimumSupportediOSVersion
                : config.minimumSupportedAndroidVersion;
            setMustUpdate(compareVersions(currentVersion, minimumSupportedVersion) < 0);
            const hasUpdate = compareVersions(currentVersion, latestVersion) < 0;

            if (!hasUpdate) return;

            setStoreUrl(isIOS ? config.iosStoreUrl : config.androidStoreUrl);
            setUpdateMessage(
                config.message ?? (mustUpdate ? "This version is no longer supported." : "A new version is available.")
            );
            setShowUpdatePrompt(true);
        };

        checkForUpdate().catch(error => {
            // A failed version check should not prevent the app from loading.
            console.error("Native app version check failed", error);
        });

        return () => {
            isDisposed = true;
        };
    }, []);

    const confirmUpdatePrompt = () => {
        if (!storeUrl) return;

        Browser.open({ url: storeUrl }).catch(error => {
            console.error("Opening native app store failed", error);
            window.open(storeUrl, "_blank");
        });
    };

    return {
        confirmUpdatePrompt,
        showUpdatePrompt,
        updateMessage,
        hideClose: mustUpdate,
        closeHandler: () => setShowUpdatePrompt(false),
    };
};
